import { Controller } from "@hotwired/stimulus"

// Print / PDF export of the collective report. Before printing, the table is
// fitted to the page width (largest font that fits, padding by column count)
// and restored afterwards, so the on-screen view is unaffected.

// A4 geometry → printable width the table auto-fit targets. Derived from a
// single margin constant so the @page rule and these widths can't drift out of
// sync. (CSS px is defined as 1/96in.)
const PAGE_MARGIN_MM = 10   // must match `margin` in the @page rule in the view
const FIT_HEADROOM_PX = 8   // shave a few px so the table never overflows by a hair

function printableWidthPx(pageWidthMm) {
  return Math.round((pageWidthMm - 2 * PAGE_MARGIN_MM) / 25.4 * 96) - FIT_HEADROOM_PX
}

const FIT_TARGET_WIDTH = printableWidthPx(297)          // A4 landscape (default, used for PDF)
const FIT_TARGET_WIDTH_PORTRAIT = printableWidthPx(210) // A4 portrait

const MIN_FONT = 5
const MAX_FONT = 16

// Horizontal cell padding based on how many columns there are: roomy when few
// cycles are compared, tighter as the table fills up. columnCount includes the
// label column and the "Suma" column.
function cellPaddingForColumns(columnCount) {
  if (columnCount <= 4) return 6   // up to ~2 cycles
  if (columnCount <= 5) return 5   // ~3 cycles
  if (columnCount <= 6) return 4   // ~4 cycles
  if (columnCount <= 8) return 3   // ~5-6 cycles
  if (columnCount <= 10) return 2  // ~7-8 cycles
  return 1                         // many cycles
}

export default class extends Controller {
  static targets = ["content", "pageStyle"]

  connect() {
    this.printWidth = FIT_TARGET_WIDTH
    this.beforePrint = () => this.fitTableToWidth(this.printWidth)
    this.afterPrint = () => this.restoreTable()
    window.addEventListener("beforeprint", this.beforePrint)
    window.addEventListener("afterprint", this.afterPrint)
  }

  disconnect() {
    window.removeEventListener("beforeprint", this.beforePrint)
    window.removeEventListener("afterprint", this.afterPrint)
  }

  // data-report-print-orientation-param="landscape" | "portrait"
  print({ params: { orientation } }) {
    this.pageStyleTarget.textContent = `@page { size: A4 ${orientation}; margin: ${PAGE_MARGIN_MM}mm; }`
    this.printWidth = orientation === "portrait" ? FIT_TARGET_WIDTH_PORTRAIT : FIT_TARGET_WIDTH
    window.print()
  }

  downloadPdf() {
    this.fitTableToWidth(FIT_TARGET_WIDTH)
    const options = {
      margin: 1,
      filename: "raport_zbiorowy.pdf",
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "mm", format: "a4", orientation: "landscape" }
    }
    window.html2pdf().set(options).from(this.contentTarget).save()
      .then(() => this.restoreTable())
      .catch(() => this.restoreTable())
  }

  get table() {
    return this.contentTarget.querySelector("table")
  }

  // Pick the largest font (capped at MAX_FONT) at which the content still fits
  // the page width, then lock the table to the page width so it spans
  // edge-to-edge.
  fitTableToWidth(targetWidth) {
    const table = this.table
    this.saved = {
      fontSize: table.style.fontSize,
      width: table.style.width,
      cellPad: table.style.getPropertyValue("--cell-pad")
    }

    const columnCount = table.querySelectorAll("tr:first-child > th").length
    table.style.setProperty("--cell-pad", cellPaddingForColumns(columnCount) + "px")

    // Measure against the natural (content-sized) width, not the stretched
    // 100% width, so we know how much room the columns actually need.
    table.style.width = "auto"

    let size = MAX_FONT
    table.style.fontSize = size + "px"
    while (size > MIN_FONT && table.scrollWidth > targetWidth) {
      size -= 0.5
      table.style.fontSize = size + "px"
    }

    table.style.width = targetWidth + "px"
  }

  restoreTable() {
    if (!this.saved) return

    const table = this.table
    table.style.fontSize = this.saved.fontSize || ""
    table.style.width = this.saved.width || ""
    if (this.saved.cellPad) {
      table.style.setProperty("--cell-pad", this.saved.cellPad)
    } else {
      table.style.removeProperty("--cell-pad")
    }
  }
}
